import type { BannerTheme } from './types';

/**
 * Normalizes a hex color string to valid #rrggbb format.
 * Handles double hashes, missing hashes, and invalid values.
 */
function normalizeHexColor(color: string | undefined | null, fallback: string): string {
  if (!color || typeof color !== 'string') return fallback;

  // Remove all # characters and whitespace
  let hex = color.replace(/[#\s]/g, '').toLowerCase();

  // If empty after cleanup, return fallback
  if (!hex) return fallback;

  // If it's a 3-character hex, expand to 6 characters
  if (hex.length === 3 && /^[0-9a-f]{3}$/.test(hex)) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  // Validate it's a proper 6-character hex
  if (hex.length === 6 && /^[0-9a-f]{6}$/.test(hex)) {
    return '#' + hex;
  }

  return fallback;
}

const defaultTheme: BannerTheme = {
  position: 'bottom',
  layout: 'bar',
  primaryColor: '#06b6d4',
  secondaryColor: '#f3f4f6',
  textColor: '#1f2937',
  backgroundColor: '#ffffff',
  borderRadius: 8,
  fontFamily: 'system-ui, -apple-system, sans-serif',
  fontSize: 14,
};

export function createStyles(theme?: BannerTheme): string {
  const t = { ...defaultTheme, ...theme };

  // Normalize all color values
  const primaryColor = normalizeHexColor(t.primaryColor, defaultTheme.primaryColor);
  const secondaryColor = normalizeHexColor(t.secondaryColor, defaultTheme.secondaryColor);
  const textColor = normalizeHexColor(t.textColor, defaultTheme.textColor);
  const backgroundColor = normalizeHexColor(t.backgroundColor, defaultTheme.backgroundColor);

  const positionStyles = getPositionStyles(t.position);

  return `
/* Cookied Cookie Banner Styles */
/* Define CSS variables on both banner and modal so they're accessible to both */
#cookied-banner,
#cookied-preferences-modal {
  --cookied-primary: ${primaryColor};
  --cookied-secondary: ${secondaryColor};
  --cookied-text: ${textColor};
  --cookied-bg: ${backgroundColor};
  --cookied-radius: ${t.borderRadius}px;
  --cookied-font: ${t.fontFamily};
  --cookied-font-size: ${t.fontSize}px;
}

#cookied-banner {
  position: fixed;
  ${positionStyles}
  z-index: 999999;
  font-family: var(--cookied-font, ${t.fontFamily});
  font-size: var(--cookied-font-size, ${t.fontSize}px);
  color: var(--cookied-text, ${textColor});
  animation: cookied-slide-in 0.3s ease-out;
}


#cookied-banner.cookied-hide {
  animation: cookied-slide-out 0.3s ease-out forwards;
}

@keyframes cookied-slide-in {
  from {
    opacity: 0;
    transform: translateY(${t.position.includes('top') ? '-20px' : '20px'});
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes cookied-slide-out {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(${t.position.includes('top') ? '-20px' : '20px'});
  }
}

.cookied-container {
  background: var(--cookied-bg, ${backgroundColor});
  border-radius: var(--cookied-radius, ${t.borderRadius}px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  padding: 16px 20px;
  max-width: ${t.layout === 'bar' ? '100%' : '420px'};
  ${t.layout === 'bar' ? 'width: 100%;' : ''}
}

.cookied-content {
  display: flex;
  flex-direction: ${t.layout === 'bar' ? 'row' : 'column'};
  align-items: ${t.layout === 'bar' ? 'center' : 'stretch'};
  gap: 16px;
}

.cookied-text {
  flex: 1;
}

.cookied-title {
  font-weight: 600;
  font-size: calc(var(--cookied-font-size, ${t.fontSize}px) + 2px);
  margin: 0 0 6px 0;
  color: var(--cookied-text, ${textColor});
}

.cookied-description {
  margin: 0;
  line-height: 1.5;
  color: var(--cookied-text, ${textColor});
  opacity: 0.85;
}

.cookied-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  ${t.layout === 'bar' ? '' : 'margin-top: 8px;'}
}

.cookied-btn {
  padding: 10px 18px;
  border-radius: calc(var(--cookied-radius, ${t.borderRadius}px) - 2px);
  font-family: var(--cookied-font, ${t.fontFamily});
  font-size: var(--cookied-font-size, ${t.fontSize}px);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  white-space: nowrap;
}

.cookied-btn:focus {
  outline: 2px solid var(--cookied-primary, ${primaryColor});
  outline-offset: 2px;
}

.cookied-btn-primary {
  background: var(--cookied-primary, ${primaryColor});
  color: white;
}

.cookied-btn-primary:hover {
  filter: brightness(0.9);
}

.cookied-btn-secondary {
  background: var(--cookied-secondary, ${secondaryColor});
  color: var(--cookied-text, ${textColor});
}

.cookied-btn-secondary:hover {
  filter: brightness(0.95);
}

.cookied-btn-link {
  background: transparent;
  color: var(--cookied-text, ${textColor});
  text-decoration: underline;
  padding: 10px 12px;
}

.cookied-btn-link:hover {
  opacity: 0.7;
}

.cookied-branding {
  margin-top: 10px;
  text-align: center;
  font-size: 11px;
  opacity: 0.6;
}

.cookied-branding a {
  color: inherit;
  text-decoration: none;
}

.cookied-branding a:hover {
  text-decoration: underline;
}

/* Preferences Modal */
#cookied-preferences-modal {
  position: fixed;
  inset: 0;
  z-index: 9999999;
  isolation: isolate;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  animation: cookied-fade-in 0.2s ease-out;
}

#cookied-preferences-modal.cookied-hide {
  animation: cookied-fade-out 0.2s ease-out forwards;
}

@keyframes cookied-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes cookied-fade-out {
  from { opacity: 1; }
  to { opacity: 0; }
}

.cookied-modal {
  position: relative;
  z-index: 9999999;
  transform: translateZ(0);
  background: var(--cookied-bg, ${backgroundColor});
  border-radius: var(--cookied-radius, ${t.borderRadius}px);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  padding: 24px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  animation: cookied-modal-in 0.3s ease-out;
}

@keyframes cookied-modal-in {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.cookied-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.cookied-modal-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.cookied-modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  opacity: 0.6;
}

.cookied-modal-close:hover {
  opacity: 1;
}

.cookied-categories {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
}

.cookied-category {
  padding: 16px;
  background: var(--cookied-secondary, ${secondaryColor});
  border-radius: calc(var(--cookied-radius, ${t.borderRadius}px) - 2px);
}

.cookied-category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.cookied-category-title {
  font-weight: 600;
  font-size: calc(var(--cookied-font-size, ${t.fontSize}px) + 1px);
}

.cookied-category-description {
  font-size: calc(var(--cookied-font-size, ${t.fontSize}px) - 1px);
  opacity: 0.8;
  line-height: 1.4;
}

/* Toggle Switch */
.cookied-toggle {
  position: relative;
  width: 44px;
  height: 24px;
}

.cookied-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.cookied-toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 24px;
  transition: 0.2s;
}

.cookied-toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}

.cookied-toggle input:checked + .cookied-toggle-slider {
  background: var(--cookied-primary, ${primaryColor});
}

.cookied-toggle input:checked + .cookied-toggle-slider:before {
  transform: translateX(20px);
}

.cookied-toggle input:disabled + .cookied-toggle-slider {
  opacity: 0.6;
  cursor: not-allowed;
}

.cookied-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* Modal Open State - Ensure modal appears above all page content */
body.cookied-modal-open {
  /* Prevent scroll when modal is open */
  overflow: hidden;
}

/* Lower all page content when modal is open to prevent z-index bleeding */
body.cookied-modal-open > *:not(#cookied-preferences-modal):not(#cookied-banner):not(script):not(style):not(link) {
  position: relative !important;
  z-index: 0 !important;
}

/* Reset transforms on all elements when modal is open to prevent stacking context issues */
body.cookied-modal-open *:not(#cookied-preferences-modal):not(#cookied-preferences-modal *):not(#cookied-banner):not(#cookied-banner *) {
  transform: none !important;
  will-change: auto !important;
}

/* Responsive */
@media (max-width: 640px) {
  #cookied-banner {
    left: 8px;
    right: 8px;
    bottom: 8px;
  }
  
  .cookied-container {
    padding: 14px 16px;
  }
  
  .cookied-content {
    flex-direction: column;
  }
  
  .cookied-buttons {
    width: 100%;
  }
  
  .cookied-btn {
    flex: 1;
    text-align: center;
  }
}
`;
}

function getPositionStyles(position: BannerTheme['position']): string {
  switch (position) {
    case 'top':
      return 'top: 16px; left: 16px; right: 16px;';
    case 'bottom':
      return 'bottom: 16px; left: 16px; right: 16px;';
    case 'center':
      return 'top: 50%; left: 50%; transform: translate(-50%, -50%);';
    case 'bottom-left':
      return 'bottom: 16px; left: 16px; max-width: 420px;';
    case 'bottom-right':
      return 'bottom: 16px; right: 16px; max-width: 420px;';
    default:
      return 'bottom: 16px; left: 16px; right: 16px;';
  }
}

